import React from 'react';
import { Alert, Modal, ModalBody, ModalHeader, Table } from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import { canSeeBilling } from '../../utils/invoices';
import { usageMonthLabel, invoiceText, usd, vatNote, hasUsage, NO_USAGE } from '../../utils/usage';
import './usage-tab.scss';

// The Account page's Usage tab (laravel BillingController@usage, App\Usage): month by month, newest first, what the
// invoices charge for (guards billed, duties, duty starts, the amount), counted by the invoicing code itself, so it always
// matches the invoices. Clicking a month shows it per guard. A company admin sees their own account (the server decides);
// the super admin the account chosen in the admin website (localStorage companyId), as on the Billing tab.
class UsageTab extends React.Component {
	constructor(props) {
		super(props);
		this.state = { loading: true, error: '', data: null, month: null, monthData: null, monthError: '' };
	}

	componentDidMount() {
		this.load();
	}

	level() {
		try {
			return localStorage.getItem('admin_level');
		} catch (e) {
			return null;
		}
	}

	request(extra) {
		const request = { ...extra };
		if (this.level() === 'a') {
			try {
				request.company_id = localStorage.getItem('companyId');
			} catch (e) {
				// storage unavailable: the server refuses without a company
			}
		}
		return request;
	}

	load() {
		if (!canSeeBilling(this.level())) {
			this.setState({ loading: false, error: 'Usage is shown to the account admin and to Mobiliis only.' });
			return;
		}
		auth.getUsage(this.request({}))
			.then((res) => {
				if (res.data.code == 200) {
					this.setState({ loading: false, data: res.data });
				} else {
					this.setState({ loading: false, error: formatServerMessage(res.data.message, 'Usage could not be loaded.') });
				}
			})
			.catch(() => this.setState({ loading: false, error: 'Usage could not be loaded. Please try again.' }));
	}

	openMonth(row) {
		this.setState({ month: row, monthData: null, monthError: '' });
		auth.getUsage(this.request({ month: row.month }))
			.then((res) => {
				if (this.state.month !== row) {
					return;
				}
				if (res.data.code == 200) {
					this.setState({ monthData: res.data.usage });
				} else {
					this.setState({ monthError: formatServerMessage(res.data.message, 'This month could not be loaded.') });
				}
			})
			.catch(() => this.state.month === row && this.setState({ monthError: 'This month could not be loaded. Please try again.' }));
	}

	renderMonth() {
		const { month, monthData, monthError } = this.state;
		if (!month) {
			return null;
		}
		const close = () => this.setState({ month: null, monthData: null });
		return (
			<Modal isOpen={true} toggle={close} size="lg">
				<ModalHeader toggle={close}>{usageMonthLabel(month)} per guard</ModalHeader>
				<ModalBody>
					{monthError && <Alert color="danger">{monthError}</Alert>}
					{!monthError && !monthData && <p>Loading...</p>}
					{monthData && monthData.guards.length === 0 && <p>{NO_USAGE}</p>}
					{monthData && monthData.guards.length > 0 && (
						<React.Fragment>
							<div className="table-responsive">
								<Table size="sm" className="usage-table mb-2">
									<thead>
										<tr><th>Guard</th><th className="text-right">Duties</th><th className="text-right">Duty starts</th><th className="text-right">Amount</th></tr>
									</thead>
									<tbody>
										{monthData.guards.map((guard) => (
											<tr key={guard.guard_id === null ? 'none' : guard.guard_id}>
												<td>{guard.name}</td>
												<td className="text-right">{guard.duties}</td>
												<td className="text-right">{guard.duty_starts}</td>
												<td className="text-right text-nowrap">{usd(guard.amount_usd)}</td>
											</tr>
										))}
									</tbody>
									<tfoot>
										<tr>
											<th>Total ({monthData.totals.guards} {monthData.totals.guards === 1 ? 'guard' : 'guards'})</th>
											<th className="text-right">{monthData.totals.duties}</th>
											<th className="text-right">{monthData.totals.duty_starts}</th>
											<th className="text-right text-nowrap">{usd(monthData.totals.amount_usd)}</th>
										</tr>
									</tfoot>
								</Table>
							</div>
							<p className="text-muted small mb-0">
								Each guard's amount is their duty starts at the price of each, rounded to the cent. The month's amount is
								the invoice's, rounded per invoice line, so the guards' amounts can add up to a cent or so more or less.
								{vatNote(monthData.vat_rate) && ' ' + vatNote(monthData.vat_rate)}
							</p>
						</React.Fragment>
					)}
				</ModalBody>
			</Modal>
		);
	}

	render() {
		const { loading, error, data } = this.state;
		if (loading) {
			return <p className="mt-3">Loading usage...</p>;
		}
		const months = data ? data.usage.months : [];
		const superAdmin = this.level() === 'a';
		return (
			<div className="mt-3">
				{error && <Alert color="danger">{error}</Alert>}
				{data && superAdmin && <p className="text-muted small mb-2">Usage of {data.company.name} (as the super admin).</p>}
				{data && !hasUsage(months) && <p>{NO_USAGE}</p>}
				{data && hasUsage(months) && (
					<React.Fragment>
						<p className="text-muted small mb-2">
							What each month's invoice charges for: each duty start, at the price for its client. Click a month to see it per guard.
							{vatNote(data.usage.vat_rate) && ' ' + vatNote(data.usage.vat_rate)}
						</p>
						<div className="table-responsive">
							<Table hover size="sm" className="usage-table">
								<thead>
									<tr>
										<th>Month</th>
										<th className="text-right">Guards billed</th>
										<th className="text-right">Duties</th>
										<th className="text-right">Duty starts</th>
										<th className="text-right">Amount</th>
										<th>Invoice</th>
									</tr>
								</thead>
								<tbody>
									{months.map((row) => (
										<tr key={row.month} className="usage-row" tabIndex={0} role="button"
											aria-label={'Show ' + usageMonthLabel(row) + ' per guard'}
											onClick={() => this.openMonth(row)}
											onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.openMonth(row); } }}>
											<td className="text-nowrap">
												<span className="usage-month">{usageMonthLabel(row)}</span>
											</td>
											<td className="text-right">{row.guards}</td>
											<td className="text-right">{row.duties}</td>
											<td className="text-right">{row.duty_starts}</td>
											<td className="text-right text-nowrap">{usd(row.amount_usd)}</td>
											<td className="small usage-invoice">{invoiceText(row)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					</React.Fragment>
				)}
				{this.renderMonth()}
			</div>
		);
	}
}

export default UsageTab;
